/**
 * RolesScreen — role DEFINITION (`roles` route, System-Administrator-only rail).
 * Extracted verbatim from screens/people/StaffAndRoles.tsx on 2026-10-16 (Pat)
 * when the `people` screen was removed; no behaviour change. Served by the
 * identity team API (lib/team-roles-api): listing needs `team.read`,
 * create/edit/delete custom roles needs `team.write`, checked inside RolesTab.
 *
 * Data: bound — via useTeamRoles / createTeamRoleOp / updateTeamRoleOp /
 * deleteTeamRoleOp (unchanged from the source file).
 */
import { useState } from "react";
import { AddRowButton, ConfirmDialog, Dropdown, IconTile, InlineEmptyState, PageState, Banner, Btn, Card, Checkbox, Field, Icon, Input, Pill, SectionLabel, T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useT, t as i18nT } from "../../lib/i18n";
import { useTeamRoles, createTeamRoleOp, updateTeamRoleOp, deleteTeamRoleOp, teamRoleErrorMessage, type TeamRole } from "../../lib/team-roles-api";

function RoleCapabilityList({ role, checked, disabled, onToggle }: {
  role: TeamRole | undefined;
  checked: string[];
  disabled?: boolean;
  onToggle?: (capability: string) => void;
}) {
  if (!role) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
      {role.capabilities.map((capability, i) => (
        <label key={capability} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n1, cursor: disabled ? "default" : "pointer" }}>
          <Checkbox checked={checked.includes(capability)} disabled={disabled} onChange={() => onToggle?.(capability)} />
          <span style={{ minWidth: 0 }}>{i18nT(role.permissions[i] ?? capability, capability)}</span>
        </label>
      ))}
    </div>
  );
}

interface RoleDraft { name: string; basedOn: string; capabilities: string[] }

function RolesTab({ roles }: { roles: TeamRole[] }) {
  const t = useT();
  /* Reading is the route's gate (`team.read` = identity.team.read). Every write
     needs `team.write`, the shell's projection of identity.team.manage — and a
     viewer without it gets no write controls at all, not disabled ones. */
  const canManage = can("team.write");
  const builtIns = roles.filter((r) => !r.isCustom);
  const [selected, setSelected] = useState<string>(roles[0]?.key ?? "");
  const [creating, setCreating] = useState(false);
  /* Edits are staged and committed by Save, so an accidental click isn't an
     immediate change for everyone holding the role. */
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [busy, setBusy] = useState<"save" | "create" | "delete" | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /* The backend's own refusal (name taken, role still assigned, capability
     outside the base role), shown where the change was attempted. */
  const [problem, setProblem] = useState<string | null>(null);

  const role = creating ? undefined : roles.find((r) => r.key === selected) ?? roles[0];
  const baseOf = (key: string | null | undefined) => builtIns.find((r) => r.key === key);
  /* A custom role can only hold capabilities of its built-in base, so the base
     is the list it is edited against. A built-in role lists its own. */
  const base = creating ? baseOf(draft?.basedOn) : role?.isCustom ? baseOf(role.basedOn) ?? role : role;
  const editing = (): RoleDraft | null =>
    draft ?? (role ? { name: role.label, basedOn: role.basedOn ?? "", capabilities: role.capabilities } : null);
  const checked = draft?.capabilities ?? role?.capabilities ?? [];
  const dirty = !!role && !!draft && (draft.name.trim() !== role.label
    || draft.capabilities.length !== role.capabilities.length
    || draft.capabilities.some((c) => !role.capabilities.includes(c)));

  const pick = (key: string) => { setSelected(key); setCreating(false); setDraft(null); setProblem(null); };
  const startCreate = (basedOn: string = builtIns[0]?.key ?? "") => {
    const from = baseOf(basedOn);
    setCreating(true);
    setProblem(null);
    setDraft({ name: "", basedOn, capabilities: from ? [...from.capabilities] : [] });
  };
  const toggle = (capability: string) => {
    const current = editing();
    if (!canManage || !current) return;
    setDraft({
      ...current,
      capabilities: current.capabilities.includes(capability)
        ? current.capabilities.filter((c) => c !== capability)
        : [...current.capabilities, capability],
    });
  };
  const rename = (name: string) => {
    const current = editing();
    if (current) setDraft({ ...current, name });
  };

  const create = async () => {
    if (!draft) return;
    setBusy("create");
    setProblem(null);
    try {
      const result = await createTeamRoleOp({ name: draft.name.trim(), basedOn: draft.basedOn, permissions: draft.capabilities });
      /* Stale = the tenant changed while this was in flight. It is not a
         refusal and it is not this tenant's success: say nothing. */
      if (result.status === "stale") return;
      setCreating(false);
      setDraft(null);
      if (result.role) setSelected(result.role.key);
      toast({ tone: "success", title: t("staffRoles.roleCreated", "Role created"), subtitle: result.role?.label ?? draft.name.trim() });
    } catch (error) {
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    if (!role || !draft || !dirty) return;
    setBusy("save");
    setProblem(null);
    try {
      const result = await updateTeamRoleOp(role.key, { name: draft.name.trim(), permissions: draft.capabilities });
      if (result.status === "stale") return;
      setDraft(null);
      toast({ tone: "success", title: t("staffRoles.roleSaved", "Role saved"), subtitle: draft.name.trim() });
    } catch (error) {
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!role) return;
    setBusy("delete");
    setProblem(null);
    try {
      const result = await deleteTeamRoleOp(role.key);
      if (result.status === "stale") return;
      setConfirmingDelete(false);
      setDraft(null);
      setSelected(builtIns[0]?.key ?? "");
      toast({ tone: "success", title: t("staffRoles.roleDeleted", "Role deleted"), subtitle: role.label });
    } catch (error) {
      setConfirmingDelete(false);
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const roleHue = (i: number) =>
    /* Illustrative role hues — the tertiary ramp, which is what the token set
       reserves for chart/illustration colour. */
    [T.tPurple, T.tOrange, T.tTurquoise, T.tGreen, T.tDarkBlue, T.tPink, T.tRed][i % 7];
  const draftValid = !!draft && !!draft.name.trim() && draft.capabilities.length > 0;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: T.n1 }}>{t("staffRoles.manageRoles", "Manage roles")}</h3>
        <p style={{ margin: 0, fontSize: 12, color: T.soft, lineHeight: 1.5, maxWidth: 720 }}>
          {t("staffRoles.manageTeamRolesBlurb", "Built-in roles come with the workspace and can't be changed. A custom role starts from a built-in role and can only narrow what it grants.")}
        </p>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Card style={{ width: 232, flexShrink: 0, padding: 8 }}>
          {roles.map((r, i) => {
            const on = !creating && r.key === role?.key;
            return (
              /* ui-raw: full-width clickable list row (avatar + two lines) — no row-as-button component exists */
              <button key={r.key} type="button" onClick={() => pick(r.key)}
                style={{ display: "flex", gap: 8, width: "100%", padding: "8px 12px", borderRadius: 8, border: "none", background: on ? T.p4 : "transparent", cursor: "pointer", textAlign: "left", fontFamily: T.font, alignItems: "flex-start" }}>
                <span style={{ width: 12, height: 12, borderRadius: 4, background: roleHue(i), flexShrink: 0, marginTop: 4 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: on ? 700 : 600, color: on ? T.p1 : T.n1 }}>{r.label}</span>
                  <span style={{ display: "block", fontSize: 12, color: r.memberCount ? T.soft : T.orange1, marginTop: 2 }}>
                    {r.memberCount ? r.memberCount : t("staffRoles.noHolders", "No holders")} · {r.capabilities.length} {t("staffRoles.caps", "Caps")}
                    {r.isCustom ? <> · {t("staffRoles.customRole", "Custom")}</> : null}
                  </span>
                </span>
              </button>
            );
          })}
          {canManage ? (
            <div style={{ marginTop: 4 }}>
              <AddRowButton disabled={busy !== null || builtIns.length === 0} onClick={() => startCreate()}>
                {t("staffRoles.newRole", "New role")}
              </AddRowButton>
            </div>
          ) : null}
        </Card>

        <Card style={{ flex: 1, minWidth: 340, padding: 16 }}>
          {problem ? (
            <Banner tone="danger" size="compact" style={{ marginBottom: 12 }}>{problem}</Banner>
          ) : null}
          {creating && draft ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <Pill tone="primary">{t("staffRoles.newRole", "New role")}</Pill>
                <span style={{ fontSize: 13, color: T.n2 }}>{draft.capabilities.length} {t("staffRoles.capabilities", "capabilities")}</span>
                <span style={{ flex: 1 }} />
                <Btn size="sm" variant="link" disabled={busy !== null} onClick={() => { setCreating(false); setDraft(null); setProblem(null); }}>
                  {t("common.actions.cancel", "Cancel")}
                </Btn>
                <Btn size="sm" disabled={busy !== null || !draftValid || !draft.basedOn} onClick={() => void create()}>
                  {busy === "create" ? t("staffRoles.saving", "Saving…") : t("staffRoles.createRole", "Create role")}
                </Btn>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
                <Field label={t("staffRoles.roleName", "Role name")}>
                  <Input value={draft.name} maxLength={80} disabled={busy !== null} onChange={(e) => rename(e.target.value)} />
                </Field>
                <Field label={t("staffRoles.basedOn", "Based on")}>
                  <Dropdown value={draft.basedOn || null} disabled={busy !== null}
                    aria-label={t("staffRoles.basedOn", "Based on")}
                    onChange={(v) => { const from = baseOf(v); setDraft({ ...draft, basedOn: v, capabilities: from ? [...from.capabilities] : [] }); }}
                    options={builtIns.map((r) => ({ value: r.key, label: r.label }))} />
                </Field>
              </div>
              <SectionLabel style={{ marginBottom: 8 }}>{t("staffRoles.whatItGrants", "What it grants")}</SectionLabel>
              <RoleCapabilityList role={base} checked={draft.capabilities} disabled={busy !== null} onToggle={toggle} />
            </>
          ) : !role ? (
            <InlineEmptyState size="sm" icon={<IconTile icon={<Icon.lock size={16} />} />}
              title={t("staffRoles.pickARole", "Pick a role")}
              description={t("staffRoles.chooseARoleToSee", "Choose a role to see what it grants.")} />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <Pill tone="primary">{role.label}</Pill>
                <span style={{ fontSize: 13, color: T.n2 }}>{checked.length} {t("staffRoles.capabilities", "capabilities")}</span>
                <span style={{ flex: 1 }} />
                {canManage && role.isCustom ? (
                  <>
                    <Btn size="sm" variant="danger" disabled={busy !== null} onClick={() => setConfirmingDelete(true)}>
                      {t("staffRoles.deleteRole", "Delete role")}
                    </Btn>
                    <Btn size="sm" disabled={busy !== null || !dirty || !draftValid} onClick={() => void save()}>
                      {busy === "save" ? t("staffRoles.saving", "Saving…") : t("staffRoles.saveChanges", "Save changes")}
                    </Btn>
                  </>
                ) : null}
                {canManage && !role.isCustom ? (
                  <Btn size="sm" variant="secondary" disabled={busy !== null} onClick={() => startCreate(role.key)}>
                    {t("staffRoles.createFromThisRole", "Create a custom role from this")}
                  </Btn>
                ) : null}
              </div>
              {role.description ? (
                <p style={{ margin: "0 0 16px", fontSize: 13, color: T.n2, lineHeight: 1.5 }}>{role.description}</p>
              ) : null}
              {role.isCustom ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, maxWidth: 420 }}>
                  {canManage ? (
                    <Field label={t("staffRoles.roleName", "Role name")}>
                      <Input value={draft?.name ?? role.label} maxLength={80} disabled={busy !== null} onChange={(e) => rename(e.target.value)} />
                    </Field>
                  ) : null}
                  <span style={{ fontSize: 12, color: T.soft }}>
                    {t("staffRoles.basedOnRole", "Based on")} {baseOf(role.basedOn)?.label ?? role.basedOn}
                  </span>
                </div>
              ) : (
                <Banner tone="info" size="compact" style={{ marginBottom: 16 }}>
                  {t("staffRoles.builtInReadOnly", "Built-in roles can't be changed.")}
                </Banner>
              )}
              <SectionLabel style={{ marginBottom: 8 }}>{t("staffRoles.whatItGrants", "What it grants")}</SectionLabel>
              <RoleCapabilityList role={base} checked={checked}
                disabled={!canManage || !role.isCustom || busy !== null} onToggle={toggle} />
              <ConfirmDialog open={confirmingDelete} tone="danger" busy={busy === "delete"}
                title={t("staffRoles.deleteRoleTitle", "Delete this role?")}
                consequence={t("staffRoles.deleteRoleConsequence", "The role is removed for the whole workspace. A role someone still holds can't be deleted — reassign them first.")}
                confirmLabel={t("staffRoles.deleteRole", "Delete role")}
                cancelLabel={t("common.actions.cancel", "Cancel")}
                onCancel={() => setConfirmingDelete(false)} onConfirm={() => void remove()} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}


export function RolesScreen() {
  const t = useT();
  const { roles, loading, error, reload } = useTeamRoles();
  return (
    <Screen rail="roles">
      {/* No PageHeader: RolesTab opens with its own "Manage roles" heading and
          blurb, so a page title above it said the same thing twice. */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px" }}>
        <PageState noun={t("staffRoles.nounRoles", "roles")} icon={<Icon.lock size={20} />}
          loading={loading} error={error} onRetry={reload} skeleton="cards" skeletonRows={4}>
          <RolesTab roles={roles} />
        </PageState>
      </div>
    </Screen>
  );
}

